import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import type { CountdownData } from '../lib/types';
import { Countdown } from './Countdown';
import { isValidEmail } from '../lib/validation';

export const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<CountdownData | null>(null);

  useEffect(() => {
    let isMounted = true;
    api
      .getCountdown()
      .then(res => {
        if (isMounted) {
          setCountdown(res);
        }
      })
      .catch(() => {
        // fail gracefully
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const showCountdown =
    Boolean(countdown) &&
    (countdown?.visibility === 'login' || countdown?.visibility === 'both') &&
    Boolean(countdown?.target);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }

    setIsSubmitting(true);

    try {
      const user = await login(trimmedEmail, password);
      // Route based on role or intended destination
      const destination = (location.state as { from?: { pathname: string } })?.from?.pathname;
      if (destination && destination !== '/login') {
        navigate(destination, { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid credentials';
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <header className="login-header">
          <h1 className="wordmark">heart</h1>
        </header>

        {showCountdown && countdown?.target && (
          <Countdown
            target={countdown.target}
            title={countdown.title}
            variant="login"
          />
        )}

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {errorMessage && (
            <div className="form-error" role="alert" aria-live="polite">
              {errorMessage}
            </div>
          )}

          <div className="form-field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-field">
            <div className="label-with-action">
              <label htmlFor="login-password">Password</label>
              <button
                type="button"
                className="text-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={0}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Entering...' : 'Enter'}
          </button>
        </form>
      </div>
    </div>
  );
};
